import React, { useEffect, useState } from 'react';

/**
 * PhotoViewerModal Component
 * 
 * Displays player photo or initials in a large modal viewer
 * 
 * Props:
 * - isOpen: Boolean - controls modal visibility
 * - playerName: String - player's name
 * - photoUrl: String - URL to player's photo (optional)
 * - onClose: Function - callback when modal closes
 */
const PhotoViewerModal = ({ isOpen, playerName, photoUrl, onClose }) => {
  const [imageError, setImageError] = useState(false);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset image error state when modal opens/closes or photo changes
  useEffect(() => {
    setImageError(false);
  }, [isOpen, photoUrl]);

  if (!isOpen) return null;

  // Calculate initials for fallback
  const nameStr = String(playerName || "Player");
  const initials = (
    nameStr
      .split(" ")
      .map((s) => s && s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("") || "P"
  ).toUpperCase();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          cursor: 'pointer'
        }}
      />

      {/* Modal Content */}
      <div
        style={{
          position: 'relative',
          background: '#fff',
          borderRadius: 16,
          maxWidth: '90vw',
          maxHeight: '90vh',
          padding: 24,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          zIndex: 10000
        }}
        onClick={(e) => e.stopPropagation()} // Prevent close when clicking content
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: 'none',
            background: '#f3f4f6',
            color: '#374151',
            fontSize: 18,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.15s',
            zIndex: 10001
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#e5e7eb';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#f3f4f6';
            e.target.style.transform = 'scale(1)';
          }}
          title="Close (ESC)"
        >
          ✕
        </button>

        {/* Player Name */}
        <div style={{
          fontSize: 20,
          fontWeight: 700,
          color: '#111827',
          textAlign: 'center',
          marginTop: 8
        }}>
          {playerName}
        </div>

        {/* Photo or Initials */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300
        }}>
          {photoUrl && !imageError ? (
            // Display photo
            <div style={{
              maxWidth: '600px',
              maxHeight: '70vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={photoUrl}
                alt={playerName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            // Display large initials (fallback)
            <div style={{
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: '#e5f3ff',
              color: '#1e3a8a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 96,
              fontWeight: 700,
              border: '4px solid #e5e7eb',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
            }}>
              {initials}
            </div>
          )}
        </div>

        {/* Error message if photo failed to load */}
        {imageError && photoUrl && (
          <div style={{
            fontSize: 13,
            color: '#dc2626',
            textAlign: 'center',
            padding: '8px 12px',
            background: '#fee2e2',
            borderRadius: 6,
            marginTop: 8
          }}>
            Photo unavailable - showing initials
          </div>
        )}

        {/* Close button at bottom */}
        <button
          onClick={onClose}
          style={{
            padding: '10px 24px',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            background: '#fff',
            color: '#374151',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.15s',
            marginTop: 8
          }}
          onMouseEnter={(e) => {
            e.target.style.background = '#f9fafb';
            e.target.style.borderColor = '#9ca3af';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = '#fff';
            e.target.style.borderColor = '#d1d5db';
          }}
        >
          Close
        </button>
      </div>

      {/* CSS animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default PhotoViewerModal;
